import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getLatestPastWebinars,
  getUpcomingWebinars,
} from '@domain/features/webinars/webinars-feature'
import { getSyncedPastWebinars } from '@domain/features/webinars/webinars-youtube-sync-feature'
import type { Webinar } from '@domain/models/webinar'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { getContentRefreshPolicy } from '@presentation/hooks/content-refresh-policy'
import { useAutoRefreshOnForeground } from '@presentation/hooks/useAutoRefreshOnForeground'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'

const HOME_LATEST_WEBINARS_LIMIT = 4

function mergeWebinarsKeepingLimit(
  preferred: Webinar[],
  fallback: Webinar[],
  limit: number,
): Webinar[] {
  const merged: Webinar[] = []
  const seenUids = new Set<string>()

  for (const webinar of preferred) {
    if (seenUids.has(webinar.uid)) {
      continue
    }
    seenUids.add(webinar.uid)
    merged.push(webinar)
  }

  for (const webinar of fallback) {
    if (seenUids.has(webinar.uid)) {
      continue
    }
    seenUids.add(webinar.uid)
    merged.push(webinar)
  }

  return merged.slice(0, Math.max(0, limit))
}

export function useHomeWebinars() {
  const ssrHome = useSsrPageData()?.home
  const { user, loading: authLoading } = useAuthUser()
  const [latestWebinars, setLatestWebinars] = useState<Webinar[]>(
    ssrHome?.latestWebinars ?? [],
  )
  const [upcomingWebinars, setUpcomingWebinars] = useState<Webinar[]>(
    ssrHome?.upcomingWebinars ?? [],
  )
  const [loading, setLoading] = useState(
    !ssrHome?.latestWebinars && !ssrHome?.upcomingWebinars,
  )
  const [refreshingLatest, setRefreshingLatest] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const shouldAutoSync = user?.isAdmin === true
  const requestIdRef = useRef(0)
  const refreshPolicy = getContentRefreshPolicy('home')

  const {
    triggerRefresh,
    lastSuccessAt,
    isRefreshing,
  } = useAutoRefreshOnForeground({
    enabled: !authLoading,
    refresh: async ({ reason }) => {
      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      const showInitialLoading =
        reason === 'mount'
        && latestWebinars.length === 0
        && upcomingWebinars.length === 0

      if (showInitialLoading) {
        setLoading(true)
      }

      try {
        setRefreshingLatest(false)
        setError(null)
        const [latest, upcoming] = await Promise.all([
          getLatestPastWebinars(HOME_LATEST_WEBINARS_LIMIT),
          getUpcomingWebinars(2),
        ])
        if (requestId !== requestIdRef.current) {
          return
        }

        setLatestWebinars(latest)
        setUpcomingWebinars(upcoming)

        if (!shouldAutoSync) {
          return
        }

        setRefreshingLatest(true)
        try {
          const latestResult = await getSyncedPastWebinars({
            count: HOME_LATEST_WEBINARS_LIMIT,
          })
          if (requestId !== requestIdRef.current) {
            return
          }

          const mergedLatestWebinars = mergeWebinarsKeepingLimit(
            latestResult.webinars,
            latest,
            HOME_LATEST_WEBINARS_LIMIT,
          )
          if (mergedLatestWebinars.length > 0 || latest.length === 0) {
            setLatestWebinars(mergedLatestWebinars)
          }
        } catch {
          // Keep fast Firestore data visible when YouTube sync fails.
        } finally {
          if (requestId === requestIdRef.current) {
            setRefreshingLatest(false)
          }
        }
      } catch (loadError) {
        if (requestId === requestIdRef.current) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load webinars.',
          )
        }
        throw loadError
      } finally {
        if (showInitialLoading && requestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    },
    staleAfterMs: refreshPolicy.staleAfterMs,
    pollIntervalMs: refreshPolicy.pollIntervalMs,
    minRefreshGapMs: refreshPolicy.minRefreshGapMs,
    maxFailureBackoffMs: refreshPolicy.maxFailureBackoffMs,
  })

  useEffect(() => {
    if (authLoading) {
      return
    }
    void triggerRefresh('mount', { force: true })
  }, [authLoading, triggerRefresh, user?.uid, shouldAutoSync])

  const refresh = useCallback(async () => {
    await triggerRefresh('manual', { force: true })
  }, [triggerRefresh])

  return {
    latestWebinars,
    upcomingWebinars,
    loading,
    refreshingLatest,
    error,
    refresh,
    lastUpdatedAt: lastSuccessAt,
    isRefreshing,
  }
}
